import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonInput,
  IonButton,
  IonText,
  IonIcon,
  IonSpinner
} from '@ionic/react';
import { 
  arrowBackOutline, 
  personOutline, 
  keyOutline, 
  documentTextOutline,
  medicalOutline
} from 'ionicons/icons';
import MedicalDataViewer from './MedicalDataViewer';

const MedicalDataViewerTestHarness = () => {
  const history = useHistory();
  const [member_id, setMemberId] = useState('');
  const [user_id, setUserId] = useState('');
  const [auth_token, setAuthToken] = useState('');
  const [userFullName, setUserFullName] = useState('');
  const [showViewer, setShowViewer] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load from localStorage on component mount
  useEffect(() => {
    const savedMemberId = localStorage.getItem('member_id');
    const savedUserId = localStorage.getItem('user_id');
    const savedAuthToken = localStorage.getItem('auth_token');
    const savedUserFullName = localStorage.getItem('userFullName');

    if (savedMemberId) setMemberId(savedMemberId);
    if (savedUserId) setUserId(savedUserId);
    if (savedAuthToken) setAuthToken(savedAuthToken);
    if (savedUserFullName) setUserFullName(savedUserFullName);
  }, []);

  const handleLaunchViewer = () => {
    if (!member_id || !user_id || !auth_token) {
      alert('Please fill in all required fields');
      return;
    }

    // Save to localStorage
    localStorage.setItem('member_id', member_id);
    localStorage.setItem('user_id', user_id);
    localStorage.setItem('auth_token', auth_token);
    localStorage.setItem('userFullName', userFullName);

    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setShowViewer(true);
    }, 1000);
  };


  if (showViewer) {
    return <MedicalDataViewer showTestIdentifiers={true} />;
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButton fill="clear" slot="start" onClick={() => history.goBack()}>
            <IonIcon icon={arrowBackOutline} />
          </IonButton>
          <IonTitle>Medical Data Viewer Test Harness</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div style={{ padding: '16px' }}>
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>
                <IonIcon icon={documentTextOutline} />
                Medical Data Viewer Test Harness
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonText>
                <p>This test harness allows you to test the Medical Data Viewer component with different member data.</p>
                <p>Fill in the required fields below and click "Launch Medical Data Viewer" to test the component.</p>
              </IonText>
            </IonCardContent>
          </IonCard>

          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Authentication Parameters</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonItem>
                <IonIcon icon={personOutline} slot="start" />
                <IonLabel position="stacked">Member ID *</IonLabel>
                <IonInput
                  value={member_id}
                  onIonInput={e => setMemberId(e.detail.value)}
                  placeholder="Enter member ID"
                  type="text"
                />
              </IonItem>

              <IonItem>
                <IonIcon icon={personOutline} slot="start" />
                <IonLabel position="stacked">User ID *</IonLabel>
                <IonInput
                  value={user_id}
                  onIonInput={e => setUserId(e.detail.value)}
                  placeholder="Enter user ID"
                  type="text"
                />
              </IonItem>

              <IonItem>
                <IonIcon icon={keyOutline} slot="start" />
                <IonLabel position="stacked">Auth Token *</IonLabel>
                <IonInput
                  value={auth_token}
                  onIonInput={e => setAuthToken(e.detail.value)}
                  placeholder="Enter auth token"
                  type="text"
                />
              </IonItem>

              <IonItem>
                <IonIcon icon={personOutline} slot="start" />
                <IonLabel position="stacked">User Full Name</IonLabel>
                <IonInput
                  value={userFullName}
                  onIonInput={e => setUserFullName(e.detail.value)}
                  placeholder="Enter user full name"
                  type="text"
                />
              </IonItem>
            </IonCardContent>
          </IonCard>

          <IonCard>
            <IonCardHeader>
              <IonCardTitle>API Endpoints</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonText>
                <h3>Medical Notes Endpoint:</h3>
                <p><code>GET /medical_note_analysis/get/{member_id}</code></p>
                
                <h3>Lab Results Endpoint:</h3>
                <p><code>GET /lab_results_analysis/get/{member_id}</code></p>
                
                <p><strong>Note:</strong> Both endpoints require authentication headers with the provided auth token.</p>
              </IonText>
            </IonCardContent>
          </IonCard>

          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Sample Data</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonText>
                <p>The component will display:</p>
                <ul>
                  <li><strong>Medical Notes:</strong> Diagnosis, medical notes, medications, procedures, risk factors, investigations, and clinical status</li>
                  <li><strong>Lab Results:</strong> Pathology results with test categories, reference ranges, and historical values</li>
                </ul>
                <p>Data is displayed in an organized, tabbed interface with expandable sections for detailed information.</p>
              </IonText>
            </IonCardContent>
          </IonCard>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            {loading ? (
              <div>
                <IonSpinner name="crescent" />
                <IonText>Launching Medical Data Viewer...</IonText>
              </div>
            ) : (
              <IonButton 
                expand="block" 
                onClick={handleLaunchViewer}
                disabled={!member_id || !user_id || !auth_token}
              >
                <IonIcon icon={medicalOutline} slot="start" />
                Launch Medical Data Viewer
              </IonButton>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default MedicalDataViewerTestHarness;
